import type { StyleRow } from './stylesFixtureData';
import { seasons, groups, brandOptions, suppliers, contactPersons } from './stylesFixtureData';

/**
 * Real persisted Styles rows for the DL-62706 epic ("Bulk edit clear fields: interactions,
 * dependency, apply & audit log") — specifically the
 * `hierarchy-dependency-and-apply-audit.spec.ts` slice (dl-62726/62727/62731, Testmo
 * 2339502/2339514/2339501).
 *
 * These must be REAL rows in `stylesData` (not an `apiTransform` GET override) for two reasons:
 * 1. dl-62731's "Apply Clear Creates Per-Style Log Entry" case needs a genuine `PUT /api/styles`
 *    bulk-update + a real refetch (and, per the mock's own DL-61932 audit-log surface, a real
 *    `GET /api/styles/:id/audit-log` read) — an ephemeral override can't round-trip either.
 * 2. dl-62726/62727 exercise the CF hierarchy cascade (`BulkEditModal.tsx`'s parent→child reset),
 *    which reads `selectedItems[].customFields` directly off the row — a shape the mock's own
 *    `styles.ts` PUT/audit handlers already know how to diff (`valuesEqual` in
 *    `stylesBulkEditFixtures.ts`).
 *
 * Hierarchy pair used: "Print Type" (customFieldId 705, mother, no `parentCustomFieldId`) /
 * "Print Placement" (customFieldId 706, child, `parentCustomFieldId: 705`) — both already defined
 * in `stylesCustomFieldDefinitions.ts` for the DL-61877 inline-edit suite; reused here rather than
 * adding a third hierarchy pair. "Screen Print" (7051) is the mother value seeded below; its
 * children are "Chest" (7061) / "Sleeve" (7062). "Embroidery" (7052) is the OTHER mother value,
 * whose children are "Back" (7063) / "Collar" (7064) — used to prove the child resets/re-scopes
 * when the mother changes to a DIFFERENT value.
 *
 * "Fit Type" (customFieldId 703, single-select AllowedValue, no hierarchy) is also seeded with a
 * value on every row below — an independent, simple field to clear for the dl-62731 apply/audit
 * case, so that test doesn't have to entangle itself with the hierarchy cascade the other two
 * tests exercise.
 *
 * All rows are `state: 'unpublished'` — `styleFieldAccess.styleSupplier`
 * (`features/styles/utils/fieldAccess.ts`) disables the bulk-edit Supplier field (and nothing
 * else) for a published style, which is irrelevant here, but every other sibling epic fixture
 * uses `'unpublished'` as its safe default and there is no reason to diverge.
 */

const PRINT_TYPE_CF_ID = 705;
const PRINT_PLACEMENT_CF_ID = 706;
const FIT_TYPE_CF_ID = 703;

const SCREEN_PRINT = { id: 7051, value: 'Screen Print' };
const EMBROIDERY = { id: 7052, value: 'Embroidery' };
const CHEST = { id: 7061, value: 'Chest' };
const SLIM = { id: 7031, value: 'Slim' };

type Overrides = Partial<StyleRow> & { id: number; name: string; customId: string };

const baseClearFieldStyleRow = (overrides: Overrides): StyleRow => ({
  description: '',
  state: 'unpublished',
  logo: '',
  season: seasons[0],
  group: groups[0],
  brand: {
    id: brandOptions[0].id,
    customId: brandOptions[0].userDefinedId ?? '',
    name: brandOptions[0].name,
    contactPersonId: contactPersons[0].id,
    contactPersonName: contactPersons[0].name,
  },
  supplier: {
    id: suppliers[0].id,
    customId: suppliers[0].customId,
    name: suppliers[0].name,
    contactPersonId: contactPersons[1].id,
    contactPersonName: contactPersons[1].name,
  },
  categories: [],
  styleColors: [],
  customFields: [],
  properties: [],
  lastUpdated: '2026-01-10T09:00:00',
  readyForExport: false,
  ...overrides,
});

/**
 * A row with the full "Print Type" (mother) → "Print Placement" (child) pair populated, plus a
 * populated "Fit Type" for the apply/audit case. Every row shares this shape; only id/name/
 * customId and (for the "diff mother" rows) the hierarchy values differ.
 */
const hierarchyPopulatedRow = (
  id: number,
  name: string,
  overrides: Partial<StyleRow> = {}
): StyleRow =>
  baseClearFieldStyleRow({
    id,
    name,
    customId: `ST-${id}`,
    customFields: [
      { id: FIT_TYPE_CF_ID, type: 'allowedvalue', value: SLIM },
      { id: PRINT_TYPE_CF_ID, type: 'nestedallowedvalue', value: SCREEN_PRINT },
      { id: PRINT_PLACEMENT_CF_ID, type: 'nestedallowedvalue', value: CHEST },
    ],
    ...overrides,
  });

/** dl-62726 (Testmo #2339502) — parent change must not show "Will be set to empty" on the child. */
export const clearFieldHierarchyParentChangeRows: StyleRow[] = [
  hierarchyPopulatedRow(95001, 'ClearFieldHierarchy ParentChange A'),
  hierarchyPopulatedRow(95002, 'ClearFieldHierarchy ParentChange B'),
];

/** dl-62727 (Testmo #2339514) — child validation blocks Next/Apply until a new child is chosen. */
export const clearFieldHierarchyValidationRows: StyleRow[] = [
  hierarchyPopulatedRow(95011, 'ClearFieldHierarchy Validation A'),
  hierarchyPopulatedRow(95012, 'ClearFieldHierarchy Validation B'),
];

/** dl-62731 (Testmo #2339501) — Apply Clear creates a per-style audit-log entry. */
export const clearFieldApplyAuditRows: StyleRow[] = [
  hierarchyPopulatedRow(95021, 'ClearFieldApplyAudit Style A'),
  hierarchyPopulatedRow(95022, 'ClearFieldApplyAudit Style B'),
];

/** All new rows for this spec — spread into `buildDefaultStylesData` in `styles.ts`. */
export const clearFieldInteractionStyleRows: StyleRow[] = [
  ...clearFieldHierarchyParentChangeRows,
  ...clearFieldHierarchyValidationRows,
  ...clearFieldApplyAuditRows,
];

export const CLEAR_FIELD_HIERARCHY_IDS = {
  printType: PRINT_TYPE_CF_ID,
  printPlacement: PRINT_PLACEMENT_CF_ID,
  fitType: FIT_TYPE_CF_ID,
  screenPrint: SCREEN_PRINT,
  embroidery: EMBROIDERY,
  chest: CHEST,
  slim: SLIM,
};

/**
 * Persisted fixture styles for the DL-62704 epic ("D2 Playwright automation — Bulk edit clear
 * fields") field-type-behavior slice (DL-62714/62715/62716/62717/62718 — Testmo
 * 2339496/2339497/2339498/2339493/2339495): per-field-type "Set empty" behaviour in the Styles
 * bulk-edit modal (text/description, optional number, date-in-calendar-footer, single-select,
 * multiselect-fixed-footer).
 *
 * These are real, persisted `stylesData` rows (added to `buildDefaultStylesData` in `styles.ts`)
 * rather than an `apiTransform` override — several of the covered tickets require asserting the
 * field is genuinely empty after "Apply changes" triggers a real `PUT /api/styles` + refetch
 * (`update-execution-and-feedback.spec.ts`'s own header explains why an `apiTransform` snapshot
 * can't support that: it would freeze the GET response and never reflect the mutation). Same
 * pattern as `buildBulkFeedbackFixtureStyles` / `buildBulkTimeoutDateFixtureStyles` in
 * `stylesBulkEditFixtures.ts`.
 *
 * Each pair below shares a unique name substring (absent from every other fixture) so a single
 * table search narrows to exactly those two rows, same convention as the sibling DL-62594 specs.
 * Both rows in a pair share the SAME existing custom-field value (not a "mixed values" case —
 * that's covered by the sibling gating-eligibility spec) so `hasExistingValue` is unambiguous.
 *
 * Custom field ids used (`stylesCustomFieldDefinitions.ts`):
 *  - 701 "Fabric Notes"     — Text       (DL-62714, stands in for the Testmo case's "Description"
 *                              field, which is `excludeFromBulkEdit` for Styles — see the file's
 *                              own header comment on `ClearFieldFixtureText` below)
 *  - 708 "Target Weight (g)" — Numeric   (DL-62715)
 *  - 702 "Delivery Date"    — Date       (DL-62716)
 *  - 703 "Fit Type"         — AllowedValue, single-select (DL-62717)
 *  - 704 "Fabric Blend"     — AllowedValue, multi-select  (DL-62718; extended in
 *                              `stylesCustomFieldDefinitions.ts` to 12 allowed values so the
 *                              dropdown's option list genuinely scrolls)
 */

const baseRow = (id: number, name: string, customFields: StyleRow['customFields']): StyleRow => ({
  id,
  name,
  customId: `ST-${id}`,
  description: '',
  state: 'unpublished', // editable — every row in this fixture set must stay bulk-editable
  logo: '',
  season: seasons[2], // SS25 — unrelated to these fields, just a valid default
  group: groups[0],
  brand: {
    id: brandOptions[0].id,
    customId: brandOptions[0].userDefinedId ?? '',
    name: brandOptions[0].name,
    contactPersonId: contactPersons[0].id,
    contactPersonName: contactPersons[0].name,
  },
  supplier: {
    id: suppliers[0].id,
    customId: suppliers[0].customId,
    name: suppliers[0].name,
    contactPersonId: contactPersons[1].id,
    contactPersonName: contactPersons[1].name,
  },
  categories: [],
  styleColors: [],
  customFields,
  properties: [],
  lastUpdated: '2026-01-01T00:00:00',
  readyForExport: false, // never mandatory-for-export-locked (dl-60044) — irrelevant to this slice
});

/**
 * DL-62714 / Testmo 2339496 — "Description Text Field Shows Set Empty When Entering Field".
 * Styles' built-in `description` field is `excludeFromBulkEdit: true`
 * (`features/styles/utils/fieldAccess.ts`) and never reaches the bulk-edit modal at all — the
 * same substitution the already-landed `navigation-and-confirm.spec.ts` (DL-62592 epic) made for
 * its own Season-not-Description field-under-test. CF 701 "Fabric Notes" is the bulk-editable
 * TEXT-type field this spec exercises instead; its "Set empty" mechanics (`renderTextField` /
 * `getBulkEditClearState`) are byte-for-byte the same code path a bulk-editable Description field
 * would use (`renderDescriptionField` calls the identical `getBulkEditClearState`).
 */
export const buildClearFieldTextFixtureStyles = (): StyleRow[] => [
  baseRow(96001, 'ClearFieldFixture Text A', [
    { id: 701, type: 'text', value: { id: 0, value: 'Machine wash only' } },
  ]),
  baseRow(96002, 'ClearFieldFixture Text B', [
    { id: 701, type: 'text', value: { id: 0, value: 'Machine wash only' } },
  ]),
];

/** DL-62715 / Testmo 2339497 — optional Numeric CF 708 "Target Weight (g)". */
export const buildClearFieldNumberFixtureStyles = (): StyleRow[] => [
  baseRow(96003, 'ClearFieldFixture Number A', [
    { id: 708, type: 'numeric', value: { id: 0, value: '250' } },
  ]),
  baseRow(96004, 'ClearFieldFixture Number B', [
    { id: 708, type: 'numeric', value: { id: 0, value: '250' } },
  ]),
];

/** DL-62716 / Testmo 2339498 — Date CF 702 "Delivery Date". */
export const buildClearFieldDateFixtureStyles = (): StyleRow[] => [
  baseRow(96005, 'ClearFieldFixture Date A', [
    { id: 702, type: 'date', value: { id: 0, value: '2026-03-15' } },
  ]),
  baseRow(96006, 'ClearFieldFixture Date B', [
    { id: 702, type: 'date', value: { id: 0, value: '2026-03-15' } },
  ]),
];

/** DL-62717 / Testmo 2339493 — optional single-select CF 703 "Fit Type" = "Regular" (id 7032). */
export const buildClearFieldDropdownFixtureStyles = (): StyleRow[] => [
  baseRow(96007, 'ClearFieldFixture Dropdown A', [
    { id: 703, type: 'allowedvalue', value: { id: 7032, value: 'Regular' } },
  ]),
  baseRow(96008, 'ClearFieldFixture Dropdown B', [
    { id: 703, type: 'allowedvalue', value: { id: 7032, value: 'Regular' } },
  ]),
];

/**
 * DL-62718 / Testmo 2339495 — multiselect CF 704 "Fabric Blend" = Cotton(7041) + Polyester(7042),
 * out of the 12 allowed values seeded in `stylesCustomFieldDefinitions.ts` (enough for the
 * dropdown's option list to need internal scrolling — the Testmo case's own point).
 */
export const buildClearFieldMultiselectFixtureStyles = (): StyleRow[] => [
  baseRow(96009, 'ClearFieldFixture Multiselect A', [
    {
      id: 704,
      type: 'allowedvalue',
      values: [
        { id: 7041, value: 'Cotton' },
        { id: 7042, value: 'Polyester' },
      ],
    },
  ]),
  baseRow(96010, 'ClearFieldFixture Multiselect B', [
    {
      id: 704,
      type: 'allowedvalue',
      values: [
        { id: 7041, value: 'Cotton' },
        { id: 7042, value: 'Polyester' },
      ],
    },
  ]),
];

/** All ten field-type-behavior fixture rows, for a single spread into `buildDefaultStylesData`. */
export const buildClearFieldBehaviorFixtureStyles = (): StyleRow[] => [
  ...buildClearFieldTextFixtureStyles(),
  ...buildClearFieldNumberFixtureStyles(),
  ...buildClearFieldDateFixtureStyles(),
  ...buildClearFieldDropdownFixtureStyles(),
  ...buildClearFieldMultiselectFixtureStyles(),
];
